import { createContext, useContext, useEffect, useState } from "react";
import api from "../lib/api";

const AuthContext = createContext(null);

export const ADMIN_ROLES = ["super_admin", "admin", "operations_manager", "finance_manager", "customer_support"];

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = logged out

  useEffect(() => {
    const hasSessionHint = localStorage.getItem("if_token") || document.cookie.includes("access_token");
    if (!hasSessionHint) { setUser(false); return; }
    api.get("/auth/me")
      .then((res) => setUser(res.data))
      .catch(() => setUser(false));
  }, []);

  const login = async (email, password, portal) => {
    const res = await api.post("/auth/login", { email, password, ...(portal ? { portal } : {}) });
    localStorage.setItem("if_token", res.data.access_token);
    setUser(res.data.user);
    return res.data.user;
  };

  const register = async (payload) => {
    const res = await api.post("/auth/register", payload);
    localStorage.setItem("if_token", res.data.access_token);
    setUser(res.data.user);
    return res.data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { /* noop */ }
    localStorage.removeItem("if_token");
    localStorage.removeItem("if_impersonator");
    setUser(false);
  };

  // 60-minute inactivity auto-logout (all roles)
  useEffect(() => {
    if (!user) return;
    const mark = () => localStorage.setItem("if_last_activity", String(Date.now()));
    mark();
    let lastWrite = 0;
    const onActivity = () => {
      const now = Date.now();
      if (now - lastWrite > 30000) { lastWrite = now; mark(); }
    };
    const events = ["click", "keydown", "scroll", "touchstart", "mousemove"];
    events.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    const iv = setInterval(() => {
      const lastAct = Number(localStorage.getItem("if_last_activity") || 0);
      if (Date.now() - lastAct > 60 * 60 * 1000) {
        sessionStorage.setItem("if_idle_msg", "1");
        logout().finally(() => { window.location.href = ADMIN_ROLES.includes(user?.role) ? "/ai-admin" : "/login"; });
      }
    }, 30000);
    return () => { events.forEach((e) => window.removeEventListener(e, onActivity)); clearInterval(iv); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, setUser, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
